import { useTranslation } from 'react-i18next';
import { CalendarClock, Gauge, Layers, Star, Trophy } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { BoardSummary } from './board-utils';

interface BoardSummaryBarProps {
  summary: BoardSummary;
  className?: string;
}

interface Tile {
  key: string;
  label: string;
  value: string;
  icon: typeof Layers;
  /** What the number means, when the label is too short to say it. */
  hint?: string;
}

/**
 * The headline numbers, recomputed from whatever the filters currently allow.
 *
 * A single strip rather than a row of cards: it shares the board's toolbar line,
 * so the numbers cost no height that the columns could have used. The labels
 * drop out below `xl`, where the icon and the title carry them.
 */
export function BoardSummaryBar({ summary, className }: BoardSummaryBarProps) {
  const { t } = useTranslation();

  const tiles: Tile[] = [
    {
      key: 'total',
      label: t('gymAdmin.routeSettingBoard.summary.total'),
      value: String(summary.total),
      icon: Layers,
    },
    {
      key: 'per-week',
      label: t('gymAdmin.routeSettingBoard.summary.perWeek'),
      value: summary.climbsPerWeek === null ? '—' : summary.climbsPerWeek.toFixed(1),
      icon: Gauge,
      hint: t('gymAdmin.routeSettingBoard.summary.perWeekHint'),
    },
    {
      key: 'age',
      label: t('gymAdmin.routeSettingBoard.summary.averageAge'),
      value:
        summary.averageAgeDays === null
          ? '—'
          : t('gymAdmin.routeSettingBoard.days', { count: Math.round(summary.averageAgeDays) }),
      icon: CalendarClock,
    },
    {
      key: 'rating',
      label: t('gymAdmin.routeSettingBoard.summary.averageStars'),
      value: summary.averageRating === null ? '—' : summary.averageRating.toFixed(1),
      icon: Star,
    },
    {
      key: 'sends',
      label: t('gymAdmin.routeSettingBoard.summary.sends'),
      value: String(summary.sendsCount),
      icon: Trophy,
    },
  ];

  return (
    <div
      className={cn(
        // min-h rather than a fixed height: on a narrow window the tiles wrap
        // onto a second line, and the strip has to grow with them.
        'flex min-h-8 flex-wrap items-center gap-x-4 gap-y-1 rounded-md border bg-muted/30 px-3',
        className
      )}
    >
      {tiles.map(({ key, label, value, icon: Icon, hint }) => (
        <span key={key} className="flex items-center gap-1.5 text-xs" title={hint ?? label}>
          <Icon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
          <span className="font-semibold tabular-nums" data-testid={`board-summary-${key}`}>
            {value}
          </span>
          <span className="hidden text-muted-foreground xl:inline">{label}</span>
        </span>
      ))}
    </div>
  );
}
